import React from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Eye } from 'lucide-react';
import { useTranslation } from '../context/TranslationContext';
import {
  isIndexedBook, bookPlatform, displayBookName,
  PLATFORM_LABEL, platformTextClass, platformBadgeClass,
} from '../utils/bookGrouping';

function ProductCard({ product, imageFit }) {
  var { t, formatPrice } = useTranslation();
  var isBook = isIndexedBook(product);
  var platform = isBook ? bookPlatform(product) : null;
  var name = isBook ? displayBookName(product) : product.name;
  var slug = product.slug;
  var price = product.price;
  var originalPrice = product.originalPrice;
  var image = product.image;
  var badge = product.badge;

  var hasDiscount = originalPrice && originalPrice > price;
  var isOutOfStock = product.outOfStock;
  // `imageFit` lets parent pages opt into 'contain' for sheet-music thumbnails
  // (where cropping cuts off the score title). Defaults to 'cover' for product photos.
  var fitClass = imageFit === 'contain'
    ? 'w-full h-full object-contain p-2 bg-white transition-transform duration-700 group-hover:scale-105'
    : 'w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700 bg-neutral-800';

  return (
    <div
      data-testid={'product-card-' + slug}
      className="group relative bg-neutral-900 rounded-2xl overflow-hidden border border-white/5 hover:border-[#d4af37]/30 transition-all duration-500"
    >
      <Link to={'/product/' + slug} className="block">
        {/* Image */}
        <div className="relative aspect-square overflow-hidden">
          <img
            src={image}
            alt={name}
            className={fitClass}
            referrerPolicy="no-referrer"
            onError={function(e) { e.target.style.display = 'none'; }}
          />
          
          {/* Badge */}
          {badge && (
            <span
              data-testid={'product-badge-' + slug}
              className={'absolute top-3 left-3 px-3 py-1.5 text-xs font-semibold rounded-full ' + 
                (badge === 'SALE' || badge === 'POPULAR'
                  ? 'bg-red-500 text-white' 
                  : badge === 'PREMIUM'
                  ? 'bg-purple-500 text-white'
                  : 'bg-[#d4af37] text-black')}
            >
              {badge}
            </span>
          )}

          {/* Pre-order: on sale before the file is indexed */}
          {product.presale && (
            <span
              data-testid={'product-preorder-' + slug}
              className="absolute top-3 right-3 px-3 py-1.5 text-[10px] font-bold tracking-wider rounded-full bg-black/80 text-[#d4af37] border border-[#d4af37]"
            >
              {t('preorder')}
            </span>
          )}

          {/* Hover Overlay */}
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
            <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center text-black">
              <Eye className="w-5 h-5" />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-4">
          {platform && (
            <span
              data-testid={'product-platform-' + slug}
              className={'inline-block mb-2 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ' + platformBadgeClass(platform)}
            >
              {PLATFORM_LABEL[platform]}
            </span>
          )}
          <h3
            data-testid={'product-name-' + slug}
            className={'text-sm font-medium transition-colors line-clamp-2 min-h-[2.5rem] mb-1 ' +
              (platform ? platformTextClass(platform) + ' group-hover:text-white' : 'text-white group-hover:text-[#d4af37]')}
          >
            {name}
          </h3>
          {product.tagline && (
            <p className="text-xs font-semibold uppercase tracking-wide text-[#d4af37] mb-2">{product.tagline}</p>
          )}

          <div className="flex items-baseline gap-2 mb-3">
            <span
              data-testid={'product-price-' + slug}
              className="text-lg font-bold text-[#d4af37]"
            >
              {formatPrice(price)}
            </span>
            {hasDiscount && (
              <span className="text-sm text-neutral-600 line-through">
                {formatPrice(originalPrice)}
              </span>
            )}
          </div>
          {product.presale && (
            <p data-testid={'product-preorder-note-' + slug} className="text-[11px] leading-snug text-[#d4af37]/90 -mt-1 mb-2">
              {t('preorderNote')}
            </p>
          )}
        </div>
      </Link>
      
      {/* Buy Now Button - Links to product detail page */}
      <div className="px-4 pb-4">
        <Link
          to={'/product/' + slug}
          data-testid={'buy-now-' + slug}
          className={'block w-full py-2.5 rounded-full text-sm font-semibold text-center transition-all ' + 
            (isOutOfStock 
              ? 'bg-neutral-800 text-neutral-600 pointer-events-none' 
              : 'bg-[#d4af37] text-black hover:bg-white')}
        >
          {isOutOfStock ? t('outOfStock') : t('buyNow')}
        </Link>
      </div>
    </div>
  );
}

export default ProductCard;
